import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LegalFooterLinks } from "../components/LegalFooterLinks";
import { toast } from "sonner";
import { Loader2, Lock, LogIn, ShieldCheck } from "lucide-react";
import { useCasaneoAuth } from "../context/CasaneoAuthContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { formatError } from "../lib/errors";


/**
 * Optional dedicated login page for the Casanéo backend.
 *
 * The main app already uses the legacy `AuthContext` on `/login` (external
 * API). This page is a "second door" for the new Casanéo session — reached
 * from Settings or from an inline banner when a backend-only feature (Channex sync,
 * import, server trash…) needs it.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function CasaneoLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const { refresh: casaneoRefresh } = useCasaneoAuth();
  // Connexion complète (Casanéo + legacy, source de données, whoami) : sinon le
  // tableau de bord (AuthContext) ne voit pas la session et affiche la landing.
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      casaneoRefresh().catch(() => { /* noop */ });
      toast.success("Connecté à Casanéo");
      navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirect = window.location.origin + "/casaneo-auth-callback";
    // Emergent's redirect flow appends "#session_id=..." — we normalise it
    // to "casaneo_session_id" in the callback page URL to keep it distinct
    // from the legacy external Google flow already in use.
    const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    window.location.href = url;
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md space-y-6" data-testid="casaneo-login-page">
        <div className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-brass/10 grid place-items-center">
            <ShieldCheck className="h-6 w-6 text-brass" />
          </div>
          <h1 className="font-serif text-3xl">Connexion à Casanéo</h1>
          <p className="text-sm text-muted-foreground">
            Accédez à votre espace agence : réservations, calendriers, propriétaires, paiements et messagerie.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input
              type="email" required autoComplete="email" autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom@casaneo.fr"
              data-testid="casaneo-login-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Mot de passe</Label>
            <Input
              type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="casaneo-login-password"
            />
          </div>
          <Button
            type="submit" disabled={loading || !email || !password}
            className="w-full bg-[#0f172a] gap-2"
            data-testid="casaneo-login-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            Se connecter
          </Button>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
          <div className="relative flex justify-center text-xs uppercase font-mono tracking-wider">
            <span className="bg-background px-3 text-muted-foreground">ou</span>
          </div>
        </div>

        <Button
          type="button" variant="outline" onClick={googleLogin}
          className="w-full gap-2 h-11"
          data-testid="casaneo-login-google"
        >
          <Lock className="h-4 w-4" />
          Continuer avec Google
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Pas encore d'espace ?{" "}
          <a href="/casaneo-register" className="text-brass hover:underline font-medium" data-testid="login-to-register">
            Créer une agence
          </a>
        </p>

        <p className="text-[11px] text-center text-muted-foreground">
          Connexion chiffrée · vos données restent strictement séparées de celles des autres agences.
        </p>
        <LegalFooterLinks />
      </div>
    </div>
  );
}
